import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search, Loader2, Handshake } from "lucide-react";
import { listLeads, createLead } from "@/lib/crmApi";
import { LeadCard } from "@/components/crm/LeadCard";
import { NewLeadDialog } from "@/components/crm/NewLeadDialog";

const VIEWS = [
  { key: "active", label: "Active" },
  { key: "on_hold", label: "On Hold" },
  { key: "lost", label: "Lost" },
  { key: "all", label: "All" },
];

export default function CRM() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [view, setView] = useState("active");
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [newOpen, setNewOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["crm-leads", view, q, country],
    queryFn: () => listLeads({ view, q, country }),
    placeholderData: (prev) => prev,
  });
  const leads = data?.leads || [];
  const counts = data?.counts || {};

  const createMut = useMutation({
    mutationFn: createLead,
    onSuccess: (lead) => { qc.invalidateQueries({ queryKey: ["crm-leads"] }); setNewOpen(false); toast.success("Lead created"); nav(`/crm/${lead.id}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create lead"),
  });

  return (
    <div data-testid="crm-page" className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center"><Handshake className="h-5 w-5" /></div>
          <div>
            <p className="text-xs font-medium text-slate-500">CRM</p>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Leads</h1>
          </div>
        </div>
        <button data-testid="new-lead-btn" onClick={() => setNewOpen(true)}
          className="h-12 px-5 rounded-full bg-slate-900 text-white font-medium inline-flex items-center gap-2 hover:bg-slate-800 active:scale-[0.98] transition-[background-color,transform]">
          <Plus className="h-5 w-5" /> New lead
        </button>
      </div>

      <div data-testid="crm-view-toggle" className="inline-flex p-1 rounded-full bg-slate-100 w-full sm:w-auto">
        {VIEWS.map((v) => (
          <button key={v.key} data-testid={`crm-view-${v.key}`} onClick={() => setView(v.key)}
            className={`flex-1 sm:flex-none h-10 px-3 sm:px-4 rounded-full text-sm whitespace-nowrap font-medium transition-[background-color,color,box-shadow] ${
              view === v.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            {v.label} <span data-testid={`crm-count-${v.key}`} className="ml-1 text-xs text-slate-400">{counts[v.key] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input data-testid="crm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer or contact"
            className="w-full h-12 pl-11 pr-4 rounded-xl bg-white border border-slate-200 focus:border-slate-400 outline-none text-sm" />
        </div>
        <select data-testid="crm-country-filter" value={country} onChange={(e) => setCountry(e.target.value)}
          className="h-12 px-4 rounded-xl bg-white border border-slate-200 text-sm sm:w-56">
          <option value="">All countries</option>
          {(data?.countries || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {isLoading && <div className="py-20 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-slate-300" /></div>}
      {!isLoading && leads.length === 0 && (
        <div data-testid="crm-empty" className="py-20 text-center rounded-2xl border border-dashed border-slate-300 bg-white">
          <p className="text-slate-500">No leads here yet.</p>
        </div>
      )}
      <div data-testid="crm-lead-list" className="space-y-3">
        {leads.map((l, i) => <LeadCard key={l.id} lead={l} idx={i} />)}
      </div>

      <NewLeadDialog open={newOpen} onClose={() => setNewOpen(false)} onCreate={(v) => createMut.mutate(v)} creating={createMut.isPending} />
    </div>
  );
}
